<template>
  <div
    class="attachment-preview-wrapper"
    @mouseenter="onEnter"
    @mouseleave="onLeave"
  >
    <!-- The icon/trigger from parent -->
    <slot />

    <!-- Popup -->
    <div
      v-if="visible"
      class="attachment-preview-popup"
      :style="popupStyle"
      @mouseenter="onPopupEnter"
      @mouseleave="onPopupLeave"
    >
      <div class="preview-header">
        <span class="preview-filename">{{ filename }}</span>
      </div>

      <div class="preview-body">

        <!-- Loading spinner -->
        <div v-if="loading" class="preview-loading">
          <div class="preview-spinner"></div>
          <span>Loading preview…</span>
        </div>

        <!-- PDF -->
        <iframe
          v-if="isPdf && !loading"
          :src="pdfUrl"
          class="preview-iframe"
          frameborder="0"
        />

        <!--
          Image: always in DOM so @load fires.
          Hidden via CSS until loaded, then popup resizes to actual image dimensions.
        -->
        <img
          v-if="isImage"
          ref="previewImg"
          :src="url"
          class="preview-image"
          :class="{ 'preview-image--hidden': loading }"
          alt="preview"
          draggable="false"
          @load="onImageLoad"
          @error="loading = false"
        />

      </div>
    </div>
  </div>
</template>

<script>
const PDF_TYPES   = ['application/pdf']
const IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml']

const PDF_MARGIN  = 24 // px top and bottom margin for PDF popup

export default {
  name: 'AttachmentPreview',

  props: {
    url:       { type: String, required: true },
    filetype:  { type: String, required: true },
    filename:  { type: String, required: true },
    container: { type: String, default: '.c-main' }
  },

  data() {
    return {
      visible:     false,
      loading:     true,
      insideIcon:  false,
      insidePopup: false,
      hoverTimer:  null,
      closeTimer:  null,
      popupWidth:  0,
      popupHeight: 0,
      popupTop:    0,
      popupLeft:   0
    }
  },

  computed: {
    isPdf() {
      return PDF_TYPES.includes(this.filetype) ||
        this.filename.toLowerCase().endsWith('.pdf')
    },

    isImage() {
      return IMAGE_TYPES.includes(this.filetype) ||
        /\.(jpe?g|png|gif|webp|svg)$/i.test(this.filename)
    },

    pdfUrl() {
      return this.url + '#toolbar=0&navpanes=0&scrollbar=1'
    },

    popupStyle() {
      // Hide popup visually while waiting for image natural size
      if (this.isImage && this.loading) {
        return { visibility: 'hidden', top: '0px', left: '0px', width: '0px', height: '0px' }
      }
      return {
        top:    this.popupTop    + 'px',
        left:   this.popupLeft   + 'px',
        width:  this.popupWidth  + 'px',
        height: this.popupHeight + 'px'
      }
    }
  },

  methods: {
    onEnter() {
      this.insideIcon = true
      clearTimeout(this.closeTimer)
      clearTimeout(this.hoverTimer)
      this.hoverTimer = setTimeout(() => this.open(), 320)
    },

    onLeave() {
      this.insideIcon = false
      clearTimeout(this.hoverTimer)
      this.scheduleClose()
    },

    onPopupEnter() {
      this.insidePopup = true
      clearTimeout(this.closeTimer)
    },

    onPopupLeave() {
      this.insidePopup = false
      this.scheduleClose()
    },

    scheduleClose() {
      this.closeTimer = setTimeout(() => {
        if (!this.insideIcon && !this.insidePopup) this.close()
      }, 120)
    },

    open() {
      if (this.visible) return
      this.loading = true
      this.visible = true
      this.$nextTick(() => {
        if (this.isPdf) {
          const popupW = window.innerWidth  * 0.5
          const popupH = window.innerHeight - PDF_MARGIN * 2
          this.setSizeAndPosition(popupW, popupH, true)
          setTimeout(() => { this.loading = false }, 800)
        }
        // Image: setSizeAndPosition called after @load fires
      })
    },

    close() {
      this.visible     = false
      this.loading     = true
      this.insideIcon  = false
      this.insidePopup = false
    },

    onImageLoad(e) {
      const img     = e.target
      const maxW    = window.innerWidth  * 0.9
      const headerH = 37
      const maxH    = window.innerHeight * 0.9 - headerH

      let w = img.naturalWidth
      let h = img.naturalHeight

      // Scale down proportionally if image exceeds max bounds
      if (w > maxW || h > maxH) {
        const ratio = Math.min(maxW / w, maxH / h)
        w = w * ratio
        h = h * ratio
      }

      this.setSizeAndPosition(w, h + headerH, false)
      this.loading = false
    },

    setSizeAndPosition(popupW, popupH, isPdf) {
      const triggerRect = this.$el.getBoundingClientRect()
      const margin      = 8

      let top, left

      if (isPdf) {
        // PDF: always vertically centered with PDF_MARGIN from top/bottom
        top  = PDF_MARGIN
        left = triggerRect.left

        // Prevent overflow right
        if (left + popupW > window.innerWidth - margin) left = window.innerWidth - popupW - margin
        // Prevent overflow left
        if (left < margin) left = margin
      } else {
        // Image: position relative to trigger like before
        top  = triggerRect.bottom + margin
        left = triggerRect.left

        if (left + popupW > window.innerWidth  - margin) left = window.innerWidth  - popupW - margin
        if (left < margin)                                left = margin
        if (top  + popupH > window.innerHeight - margin) top  = triggerRect.top - popupH - margin
        if (top  < margin)                                top  = margin
      }

      this.popupWidth  = popupW
      this.popupHeight = popupH
      this.popupTop    = top
      this.popupLeft   = left
    }
  },

  beforeDestroy() {
    clearTimeout(this.hoverTimer)
    clearTimeout(this.closeTimer)
  }
}
</script>

<style scoped>
.attachment-preview-wrapper {
  display: inline-block;
  position: relative;
}

.attachment-preview-popup {
  position: fixed;
  z-index: 9999;
  background: #fff;
  border: 1px solid #d8dbe0;
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  user-select: none;
}

.preview-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 14px;
  background: #f8f9fa;
  border-bottom: 1px solid #e8e8e8;
  flex-shrink: 0;
}

.preview-filename {
  font-size: 12px;
  font-weight: 500;
  color: #555;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: calc(100% - 32px);
}

.preview-close {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
  color: #888;
  padding: 0 2px;
  flex-shrink: 0;
}

.preview-close:hover {
  color: #333;
}

.preview-body {
  flex: 1;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.preview-loading {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  background: #fff;
  color: #888;
  font-size: 13px;
  z-index: 1;
}

.preview-spinner {
  width: 28px;
  height: 28px;
  border: 2px solid #e0e0e0;
  border-top-color: #185FA5;
  border-radius: 50%;
  animation: preview-spin 0.8s linear infinite;
}

@keyframes preview-spin {
  to { transform: rotate(360deg); }
}

.preview-iframe {
  width: 100%;
  height: 100%;
  display: block;
  border: none;
}

.preview-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  display: block;
  pointer-events: none;
}

.preview-image--hidden {
  visibility: hidden;
  position: absolute;
}
</style>
